import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row, Col, Card, Statistic, Spin, Table, Alert, Segmented, Space, Tag,
  Tooltip as AntTooltip,
} from 'antd';
import {
  RollbackOutlined, UndoOutlined, SwapOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../../libs/api';

/**
 * Credit notes on both sides of PW Renewable's trade.
 *
 * The two sides are not the same kind of record and are not netted into one
 * number on the way in. A credit raised against a customer gives back
 * revenue; stock returned to a supplier recovers cost. Netting them would
 * hide which of the two moved, which is the only thing this tab exists to
 * show, so they are kept apart and the net is stated as a third figure
 * rather than as the figure.
 */

const CLIENT_HUE = '#C96A1E';
const SUPPLIER_HUE = '#2F7FA8';
const NEUTRAL = '#667085';
const FAINT = '#9AA3B2';
const GRID = '#EEF1F5';

const money = (v) => {
  if (v == null) return '-';
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(0)}`;
};
const exact = (v) => (v == null ? '-' : `$${Number(v).toLocaleString('en-AU', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`);
const count = (v) => (v != null ? Number(v).toLocaleString('en-AU') : '-');
/** money() puts the sign inside the symbol ($-37.6K); the net needs it outside. */
const signedMoney = (v) => (v == null ? '-' : `${v < 0 ? '-' : ''}${money(Math.abs(v))}`);

/**
 * Both party cards are pinned to one height rather than sized to their own row
 * count. Eight customers against two suppliers made the right-hand card finish
 * a third of the way up the left one, which read as a rendering fault. Five
 * rows of the list show and the rest scroll: these lists answer "who are the
 * big ones", and paging put the tail behind a click without making the head
 * any easier to read.
 */
const CHART_H = 300;
const ROW_H = 39;                     // one small-table row, header included
const LIST_H = 5 * ROW_H;             // scrollable body
const LIST_BOX = LIST_H + ROW_H;      // reserved even when the list is shorter

/** Longest names get truncated on the axis, in full in the tooltip. */
const shortName = (s) => (s && s.length > 26 ? `${s.slice(0, 25)}…` : s || '-');

export default function CreditNotes({ dateRange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [side, setSide] = useState('client');

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/distribution/credit-notes', {
        params: {
          start: range[0].format('YYYY-MM-DD'),
          end: range[1].format('YYYY-MM-DD'),
        },
      });
      setData(res.data.data);
    } catch (err) {
      const resp = err.response;
      setError(resp?.data?.message || (resp ? 'Failed to load credit note data' : 'Network error — unable to reach the server.'));
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(dateRange); }, [dateRange, fetchData]);

  const s = data?.stats || {};
  const byCustomer = useMemo(() => data?.byCustomer || [], [data]);
  const bySupplier = useMemo(() => data?.bySupplier || [], [data]);
  const parked = data?.parked || { client: 0, supplier: 0 };

  const notes = side === 'client' ? (data?.clientNotes || []) : (data?.supplierNotes || []);

  /**
   * Supplier side less client side. Negative means more was given back to
   * customers than was recovered from suppliers — PW is out of pocket on
   * credits for the period. Colouring it would read as good-or-bad and on its
   * own it is neither, so the sign and the wording carry the direction.
   */
  const net = s.netCn || 0;

  const partyChart = (rows, hue, label) => (
    <ResponsiveContainer width="100%" height={CHART_H}>
      <BarChart
        data={rows.slice(0, 12).map((r) => ({ ...r, short: shortName(r.name) }))}
        layout="vertical"
        margin={{ top: 4, right: 48, left: 8, bottom: 8 }}
      >
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" tickFormatter={money} tick={{ fontSize: 11, fill: NEUTRAL }} axisLine={false} tickLine={false} />
        <YAxis
          type="category"
          dataKey="short"
          tick={{ fontSize: 11, fill: NEUTRAL }}
          width={170}
          interval={0}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: 'rgba(0,0,0,0.03)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const r = payload[0].payload;
            return (
              <div style={{
                background: '#fff', padding: 10, border: `1px solid ${GRID}`,
                fontSize: 12, lineHeight: 1.6, maxWidth: 280, boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              >
                <div><b>{r.name}</b></div>
                <div>{label}: <b>{exact(r.total)}</b></div>
                <div style={{ color: NEUTRAL }}>
                  {r.count} note{r.count === 1 ? '' : 's'}
                  {r.charge ? ` · incl. ${exact(r.charge)} charges` : ''}
                  {r.costs ? ` · incl. ${exact(r.costs)} freight` : ''}
                </div>
              </div>
            );
          }}
        />
        <Bar dataKey="total" fill={hue} radius={[0, 4, 4, 0]} barSize={16} />
      </BarChart>
    </ResponsiveContainer>
  );

  const partyColumns = (extra) => [
    { title: 'Name', dataIndex: 'name', key: 'name', ellipsis: true },
    {
      title: 'Notes', dataIndex: 'count', key: 'count', align: 'right', width: 80,
      render: count,
      sorter: (a, b) => a.count - b.count,
    },
    ...extra,
    {
      title: 'Total (ex GST)', dataIndex: 'total', key: 'total', align: 'right', width: 140,
      render: (v) => <b>{exact(v)}</b>,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.total - b.total,
    },
  ];

  const noteColumns = side === 'client' ? [
    { title: 'Credit note', dataIndex: 'number', key: 'number', width: 130, fixed: 'left' },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 110, sorter: (a, b) => (a.date || '').localeCompare(b.date || ''), defaultSortOrder: 'descend' },
    { title: 'Customer', dataIndex: 'party', key: 'party', ellipsis: true },
    { title: 'Against invoice', dataIndex: 'invoiceNumber', key: 'invoiceNumber', width: 140, render: (v, r) => (v ? <span>{v}{r.invoiceDate ? <span style={{ color: FAINT }}> · {r.invoiceDate}</span> : null}</span> : <span style={{ color: FAINT }}>—</span>) },
    { title: 'Goods', dataIndex: 'goods', key: 'goods', align: 'right', width: 120, render: exact, sorter: (a, b) => a.goods - b.goods },
    { title: 'Charges', dataIndex: 'charge', key: 'charge', align: 'right', width: 110, render: (v) => (v ? exact(v) : <span style={{ color: FAINT }}>—</span>) },
    {
      title: (
        <AntTooltip title="Cost reversed because the goods came back on the shelf. A credit with no stock returned reduces revenue only.">
          <span>Stock back <InfoCircleOutlined style={{ fontSize: 11, color: FAINT }} /></span>
        </AntTooltip>
      ),
      dataIndex: 'stockCost',
      key: 'stockCost',
      align: 'right',
      width: 120,
      render: (v) => (v ? exact(v) : <span style={{ color: FAINT }}>—</span>),
    },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 130, render: (v) => <b>{exact(v)}</b>, sorter: (a, b) => a.total - b.total },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v) => v || <span style={{ color: FAINT }}>—</span> },
  ] : [
    { title: 'Return', dataIndex: 'number', key: 'number', width: 140, fixed: 'left' },
    { title: 'Date', dataIndex: 'date', key: 'date', width: 110, sorter: (a, b) => (a.date || '').localeCompare(b.date || ''), defaultSortOrder: 'descend' },
    { title: 'Supplier', dataIndex: 'party', key: 'party', ellipsis: true },
    { title: 'Against PO', dataIndex: 'purchaseOrder', key: 'purchaseOrder', width: 130, render: (v) => v || <span style={{ color: FAINT }}>—</span> },
    { title: 'Site', dataIndex: 'warehouse', key: 'warehouse', width: 70, render: (v) => (v ? <Tag style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: FAINT }}>—</span>) },
    { title: 'Goods', dataIndex: 'goods', key: 'goods', align: 'right', width: 130, render: exact, sorter: (a, b) => a.goods - b.goods },
    {
      title: (
        <AntTooltip title="Freight and handling refunded on the return. Unleashed keeps these outside the return's subtotal, so they are added here.">
          <span>Freight <InfoCircleOutlined style={{ fontSize: 11, color: FAINT }} /></span>
        </AntTooltip>
      ),
      dataIndex: 'costs',
      key: 'costs',
      align: 'right',
      width: 110,
      render: (v) => (v ? exact(v) : <span style={{ color: FAINT }}>—</span>),
    },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 140, render: (v) => <b>{exact(v)}</b>, sorter: (a, b) => a.total - b.total },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v) => v || <span style={{ color: FAINT }}>—</span> },
  ];

  return (
    <Spin spinning={loading}>
      {error && (
        <Alert type="error" showIcon message="Credit notes unavailable" description={error} style={{ marginBottom: 16 }} closable />
      )}

      {(parked.client > 0 || parked.supplier > 0) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${parked.client + parked.supplier} parked draft${parked.client + parked.supplier === 1 ? '' : 's'} left out of every figure below`}
          description={`Unleashed holds unfinished credits as parked: ${parked.client} customer credit note${parked.client === 1 ? '' : 's'} and ${parked.supplier} supplier return${parked.supplier === 1 ? '' : 's'} in this period. They are named here rather than dropped silently, so none-parked and some-parked can be told apart.`}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <RollbackOutlined style={{ color: CLIENT_HUE, fontSize: 22, marginBottom: 8 }} />
            <Statistic
              title="Client credit notes"
              value={s.clientTotal}
              formatter={(v) => money(v)}
              valueStyle={{ color: CLIENT_HUE }}
            />
            <div style={{ fontSize: 12, color: NEUTRAL, marginTop: 4 }}>
              {count(s.clientCount)} note{s.clientCount === 1 ? '' : 's'} · reduces revenue
            </div>
            <div style={{ fontSize: 12, color: FAINT, marginTop: 2 }}>
              {money(s.clientGoods)} goods
              {s.clientCharge ? ` · ${money(s.clientCharge)} charges` : ''}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <UndoOutlined style={{ color: SUPPLIER_HUE, fontSize: 22, marginBottom: 8 }} />
            <Statistic
              title="Supplier credit notes"
              value={s.supplierTotal}
              formatter={(v) => money(v)}
              valueStyle={{ color: SUPPLIER_HUE }}
            />
            <div style={{ fontSize: 12, color: NEUTRAL, marginTop: 4 }}>
              {count(s.supplierCount)} return{s.supplierCount === 1 ? '' : 's'} · recovers cost
            </div>
            <div style={{ fontSize: 12, color: FAINT, marginTop: 2 }}>
              {money(s.supplierGoods)} goods
              {s.supplierCosts ? ` · ${money(s.supplierCosts)} freight` : ''}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <SwapOutlined style={{ color: NEUTRAL, fontSize: 22, marginBottom: 8 }} />
            <Statistic
              title="Net credit notes"
              value={net}
              formatter={(v) => signedMoney(v)}
            />
            <div style={{ fontSize: 12, color: NEUTRAL, marginTop: 4 }}>
              supplier &minus; client
            </div>
            <div style={{ fontSize: 12, color: FAINT, marginTop: 2 }}>
              {net >= 0
                ? `recovered ${money(net)} more than given back`
                : `given back ${money(Math.abs(net))} more than recovered`}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title="Client credit by customer"
            size="small"
            extra={<span style={{ fontSize: 11, color: FAINT }}>{byCustomer.length} customer{byCustomer.length === 1 ? '' : 's'}{byCustomer.length > 12 ? ' · top 12 charted' : ''}</span>}
          >
            {byCustomer.length ? partyChart(byCustomer, CLIENT_HUE, 'Credit') : (
              <div style={{ height: CHART_H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: FAINT }}>
                No client credit notes in this period
              </div>
            )}
            <div style={{ height: LIST_BOX, marginTop: 8 }}>
              <Table
                size="small"
                rowKey="name"
                dataSource={byCustomer}
                pagination={false}
                scroll={{ y: LIST_H }}
                columns={partyColumns([
                  { title: 'Goods', dataIndex: 'goods', key: 'goods', align: 'right', width: 120, render: exact },
                  { title: 'Charges', dataIndex: 'charge', key: 'charge', align: 'right', width: 110, render: (v) => (v ? exact(v) : <span style={{ color: FAINT }}>—</span>) },
                ])}
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Supplier credit by supplier"
            size="small"
            extra={<span style={{ fontSize: 11, color: FAINT }}>{bySupplier.length} supplier{bySupplier.length === 1 ? '' : 's'}{bySupplier.length > 12 ? ' · top 12 charted' : ''}</span>}
          >
            {bySupplier.length ? partyChart(bySupplier, SUPPLIER_HUE, 'Credit') : (
              <div style={{ height: CHART_H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: FAINT }}>
                No supplier returns in this period
              </div>
            )}
            <div style={{ height: LIST_BOX, marginTop: 8 }}>
              <Table
                size="small"
                rowKey="name"
                dataSource={bySupplier}
                pagination={false}
                scroll={{ y: LIST_H }}
                columns={partyColumns([
                  { title: 'Goods', dataIndex: 'goods', key: 'goods', align: 'right', width: 130, render: exact },
                  { title: 'Freight', dataIndex: 'costs', key: 'costs', align: 'right', width: 110, render: (v) => (v ? exact(v) : <span style={{ color: FAINT }}>—</span>) },
                ])}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        style={{ marginTop: 16 }}
        title={(
          <Space>
            <span>Every credit note in the period</span>
            <Segmented
              size="small"
              value={side}
              onChange={setSide}
              options={[
                { value: 'client', label: `Client (${count(s.clientCount || 0)})` },
                { value: 'supplier', label: `Supplier (${count(s.supplierCount || 0)})` },
              ]}
            />
          </Space>
        )}
        extra={(
          <span style={{ fontSize: 11, color: FAINT }}>
            {side === 'client'
              ? 'dated when the credit was raised, not when the invoice it reverses was issued'
              : 'dated on the return date, which can precede the month it was entered in'}
          </span>
        )}
      >
        <Table
          size="small"
          rowKey="number"
          dataSource={notes}
          columns={noteColumns}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 25, size: 'small', showSizeChanger: true, showTotal: (t) => `${t} note${t === 1 ? '' : 's'}` }}
          summary={(rows) => {
            if (!rows.length) return null;
            const t = (k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
            return (
              <Table.Summary.Row style={{ background: '#FAFBFC', fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={side === 'client' ? 4 : 5}>
                  Page total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{exact(t('goods'))}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  {exact(t(side === 'client' ? 'charge' : 'costs'))}
                </Table.Summary.Cell>
                {side === 'client' && (
                  <Table.Summary.Cell index={3} align="right">{exact(t('stockCost'))}</Table.Summary.Cell>
                )}
                <Table.Summary.Cell index={4} align="right">{exact(t('total'))}</Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </Spin>
  );
}
